import type { BoardState, GameEvent, GameState } from '@bioforge/game-core';
import type { PublicBoss } from '@bioforge/protocol';
import type { Taunt } from '../labels.js';

/** Данные панелей боя (макет «Соло · Босс (v2)»): счёт, микробы, способности босса, реплики. */

/** Очки шестью цифрами с пробелом между тысячами: 12460 → «012 460». */
export function formatScore(score: number): string {
  return String(Math.max(0, Math.floor(score)))
    .padStart(6, '0')
    .replace(/\B(?=(\d{3})+$)/g, ' ');
}

/** «капсулу / капсулы / капсул» после «через». */
export function capsulesWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'капсулу';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'капсулы';
  return 'капсул';
}

export interface MicrobeCount {
  readonly color: number;
  readonly count: number;
  /** Сколько из них в «Биоплёнке». */
  readonly shielded: number;
}

/** Микробы в сосуде по цветам уровня (цвета без микробов тоже — с нулём). */
export function microbeCounts(board: BoardState, colorCount: number): MicrobeCount[] {
  const counts = Array.from({ length: colorCount }, (_, color) => ({
    color,
    count: 0,
    shielded: 0,
  }));
  for (const cell of board.cells) {
    if (cell.kind !== 'virus') continue;
    const row = counts[cell.color];
    if (!row) continue;
    row.count += 1;
    if (cell.shield) row.shielded += 1;
  }
  return counts;
}

export interface BossAbilityRow {
  readonly kind: string;
  readonly name: string;
  /** Способность включена и ведёт отсчёт. */
  readonly active: boolean;
  readonly status: string;
  /** Деления отсчёта: всего и уже прошло; null — без делений. */
  readonly pips: { readonly total: number; readonly filled: number } | null;
}

/** Больше делений не рисуем — период показывает только подпись. */
const MAX_PIPS = 10;

export function bossAbilityRows(state: GameState, boss: PublicBoss): BossAbilityRow[] {
  const specs = state.rules.boss?.abilities ?? [];
  return boss.abilities.map((a, i) => {
    const every = specs[i]?.every ?? null;
    const counting = !a.done && a.countdown !== null;
    let status: string;
    if (a.done) status = 'сработала';
    else if (a.countdown !== null) status = `через ${a.countdown} ${capsulesWord(a.countdown)}`;
    else if (a.belowHp !== null) status = `проснётся при здоровье ${a.belowHp} и ниже`;
    else status = 'ждёт';
    return {
      kind: a.kind,
      name: a.name,
      active: counting,
      status,
      pips:
        counting && every !== null && every <= MAX_PIPS && a.countdown !== null
          ? { total: every, filled: Math.max(0, every - a.countdown) }
          : null,
    };
  });
}

export interface TauntState {
  /** Капсул подряд без урона по боссу. */
  readonly misses: number;
}

/** Сколько промахов подряд, чтобы босс съязвил. */
const MISSES_TO_TAUNT = 3;

/**
 * Реплика босса после шага партии: удар, «почти побеждён», защита, сон, серия промахов.
 * Сработавшая способность — не реплика, а крик («ГРРААА!»); его показывает экран.
 */
export function tauntAfter(
  events: readonly GameEvent[],
  boss: PublicBoss,
  state: TauntState,
): { readonly taunt: Taunt | null; readonly state: TauntState } {
  if (events.some((e) => e.type === 'boss-defeated')) return { taunt: null, state: { misses: 0 } };
  const damaged = events.find((e) => e.type === 'boss-damaged');
  if (damaged) {
    return { taunt: boss.hp * 10 <= boss.maxHp * 3 ? 'low' : 'hit', state: { misses: 0 } };
  }
  const blocked = events.find((e) => e.type === 'boss-blocked');
  if (blocked && blocked.type === 'boss-blocked') {
    return { taunt: blocked.reason === 'latency' ? 'sleep' : 'blocked', state: { misses: 0 } };
  }
  if (!events.some((e) => e.type === 'piece-locked')) return { taunt: null, state };
  const misses = state.misses + 1;
  if (misses >= MISSES_TO_TAUNT) return { taunt: 'miss', state: { misses: 0 } };
  return { taunt: null, state: { misses } };
}

/** Время партии «мм:сс». */
export function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
